import {
  DeleteOutlineIcon,
  NoticeOutlineIcon,
  TopOutlineIcon,
} from '@carboncopyco/icons';
import {
  Button,
  Checkbox,
  ConfirmAlert,
  DropdownMenu,
  DropdownMenuItem,
  InputText,
  MessageBox,
  SelectButton,
  TextBase,
  TextCaption,
  Toggle,
} from '@carboncopyco/ui';
import { useBoolean, useClickAway } from 'ahooks';
import { isEqual } from 'lodash-es';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { CustomChartIcon } from '../CustomChartIcon';
import type {
  ActionType,
  ICustomTemplateChart,
  IState,
  TDropdownOption,
  TemplateIconName,
} from '../types';
import { isTemplateValid } from '../utils';
import { Editor } from './Editor';
import { Preview } from './Preview';

const iconOptions: TDropdownOption[] = [
  {
    value: 'column',
    label: 'Column Chart',
    icon: 'barColored',
  },
  {
    value: 'line',
    label: 'Line Chart',
    icon: 'lineColored',
  },
  {
    value: 'pieChart',
    label: 'Pie Chart',
    icon: 'pieColored',
  },
];

export interface ChartBuilderProps {
  dispatch: React.Dispatch<ActionType>;
  state: IState;
  templates: ICustomTemplateChart[];
  onSubmit: () => void;
  onClose: () => void;
  onDelete: () => Promise<void>;
}

export const ChartBuilder = ({
  dispatch,
  state,
  onSubmit,
  onClose,
  onDelete,
  templates,
}: ChartBuilderProps) => {
  const [submitDisabled, setSubmitDisabled] = useBoolean();
  const [isDropDownVisible, setIsDropDownVisible] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(true);
  const [isConfirmModalVisible, setIsConfirmModalVisible] =
    useState<boolean>(false);
  const dropDownRef = useRef<HTMLDivElement>(null);
  const [editorValue, setEditorValue] = useState<string>(
    JSON.stringify(state.template, null, 2) || ''
  );
  const prevState = useRef<IState>(state);

  useClickAway(() => setIsDropDownVisible(false), dropDownRef);

  useEffect(() => {
    if (prevState?.current?.id !== state.id) {
      prevState.current = state;
      setEditorValue(JSON.stringify(state.template, null, 2));
    }
  }, [state]);

  function isDirty() {
    if (state.id) {
      // Checking if the form touched by the user on edit mode.
      const currTemplate = templates.find((t) => t.id === state.id);

      if (currTemplate) {
        const isFormDirty = Object.keys(state)
          .reduce((prev: boolean[], curr) => {
            const key = curr as keyof Omit<IState, 'template' | 'errors'>;
            const currValue = state[key];

            if (Object.prototype.hasOwnProperty.call(currTemplate, key)) {
              if ((key as keyof IState) === 'template') {
                prev.push(
                  !isEqual(JSON.parse(currTemplate.template), currValue)
                );
              } else {
                prev.push(!isEqual(currTemplate[key], currValue));
              }
            }

            return prev;
          }, [])
          .filter(Boolean);

        return !!isFormDirty.length;
      }
    }

    return false;
  }

  function getIconOption(): TDropdownOption {
    const icon: TemplateIconName = state.templateIcon ?? 'barColored';
    const options = iconOptions.find((t) => t.icon === icon);
    return options || iconOptions[0];
  }

  const iconOption = getIconOption();

  const handleSubmit = async () => {
    setSubmitDisabled.setTrue();
    await onSubmit();
    setSubmitDisabled.setFalse();
  };

  return (
    <>
      <div className="pl-5 w-full h-full flex flex-col pt-2.5">
        <div className="h-full w-full flex flex-col">
          <div className="flex items-center relative">
            <div className="w-64">
              <InputText
                size="lg"
                name="templateName"
                placeholder="Enter template name"
                value={state.templateName}
                onChange={(e) => {
                  dispatch({
                    type: 'SET_TEMPLATE_NAME',
                    payload: e.target.value,
                  });
                }}
              />
            </div>
            <div className="flex items-center h-full ml-auto">
              <div className="relative w-5">
                <Toggle
                  checked={state.isActive}
                  onChange={(e) => {
                    dispatch({
                      type: 'SET_IS_ACTIVE',
                      payload: e.target.checked,
                    });
                  }}
                />
              </div>
              <div className="ml-3.5 ">
                <TextCaption
                  label="Activate template"
                  color="text-ds-natural-600"
                />
              </div>
            </div>
          </div>
          <div
            ref={dropDownRef}
            className="w-64 py-5"
          >
            <TextCaption
              label="Chart Icon"
              color="text-ds-natural-300"
            />
            <DropdownMenu
              visible={isDropDownVisible}
              button={
                <SelectButton
                  label={iconOption.label}
                  rightIcon={<TopOutlineIcon />}
                  isActive={isDropDownVisible}
                  flipArrow
                  style={{ width: '100%' }}
                  onClick={() => setIsDropDownVisible((prev) => !prev)}
                >
                  <div className="mr-3">
                    <CustomChartIcon name={iconOption.icon} />
                  </div>
                </SelectButton>
              }
            >
              <div
                className="h-ds-sm-section"
                style={{
                  width: dropDownRef?.current?.offsetWidth || 190,
                }}
              >
                {iconOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    label={<TextBase label={option.label} />}
                    iconLeft={
                      <CustomChartIcon
                        name={option.icon}
                        size={20}
                      />
                    }
                    onClick={() => {
                      dispatch({
                        type: 'SET_ICON',
                        payload: option.icon,
                      });
                      setIsDropDownVisible(false);
                    }}
                  />
                ))}
              </div>
            </DropdownMenu>
          </div>
          <div className="flex justify-between items-center w-full pb-2">
            <TextCaption
              label="Highcharts configuration"
              color="text-ds-natural-300"
            />
          </div>
          <div className="flex flex-1 w-full flex-col justify-between rounded-md border-ds-natural-150 border border-solid">
            <div
              className="relative w-full overflow-hidden flex flex-col transition-all"
              style={{
                height: !showPreview ? '100%' : '65%',
              }}
            >
              <Editor
                height="100%"
                onError={(errors) => {
                  dispatch({
                    type: 'SET_ERROR',
                    payload: {
                      syntaxError: errors.length ? errors[0].message : null,
                    },
                  });
                }}
                template={editorValue}
                onChange={(template) => {
                  setEditorValue(template);

                  dispatch({
                    type: 'SET_TEMPLATE',
                    payload: {
                      ...state,
                      template: JSON.parse(template),
                    },
                  });
                }}
              />
            </div>
            {state.errors.syntaxError && (
              <div className="py-2">
                <MessageBox
                  label={state.errors.syntaxError}
                  danger
                  leftIcon={<NoticeOutlineIcon />}
                />
              </div>
            )}
            <div className="flex justify-between items-center w-full p-4">
              <Checkbox
                label={<TextCaption label="Display chart preview" />}
                name="showPreview"
                checked={showPreview}
                onChange={(e) => {
                  setShowPreview(e.target.checked);
                }}
              />
            </div>
            <div
              className="flex flex-col w-full  transition-all"
              style={{
                height: showPreview ? '35%' : '0px',
              }}
            >
              <Preview
                options={state.template}
                onError={(err) => {
                  dispatch({
                    type: 'SET_ERROR',
                    payload: { chartError: err },
                  });
                }}
              />
            </div>
          </div>
        </div>
        <div className="pt-6 py-5 flex justify-between items-center">
          <div className="h-full flex items-center">
            {state.id && (
              <Button
                label="Delete template"
                leftIcon={<DeleteOutlineIcon />}
                link
                onClick={() => {
                  setIsConfirmModalVisible(true);
                }}
              />
            )}
          </div>
          <div className="flex h-full items-center">
            <Button
              onClick={onClose}
              link
              label="Cancel"
            />
            <Button
              onClick={handleSubmit}
              label={state.id ? 'Save changes' : 'Save chart'}
              disabled={
                submitDisabled ||
                (state.id && !isDirty()) ||
                !isTemplateValid(state)
              }
            />
          </div>
        </div>
      </div>
      <ConfirmAlert
        show={isConfirmModalVisible}
        title="Delete chart template"
        description={`Are you sure you want to delete "${state.templateName}" chart template ?`}
        danger
        yesLabel="Delete"
        yesIcon={<DeleteOutlineIcon />}
        onConfirm={() => {
          setIsConfirmModalVisible(false);
          onDelete();
        }}
        onClose={() => {
          setIsConfirmModalVisible(false);
        }}
      />
    </>
  );
};
